import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import { getTheme } from "customStyles/utils";
import { settings } from "app";
import { useRedirect } from "react-admin";
import { SessionExpiredDialogService } from "services/SessionExpiredDialogService";
import { removeTokens } from "auth/security/securityUtils";
import ModalTitle, { withModalSx, modalConfirmButtonSx } from "server/components/Modals/ModalTitle";

const MESSAGE = "Поради липса на активност вашата сесия беше прекратена. Моля, влезте отново, за да продължите.";

// The old dialog, for tenants without the new UI (settings.style.modernUi).
const LegacySessionExpiredDialog = ({ open, onClose }) => {
  const theme = getTheme(settings.tenant);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      sx={{
        "& .MuiDialog-paper": {
          padding: { xs: "1rem", sm: "2rem 3rem" },
        },
      }}
    >
      <DialogTitle fontWeight={800}>
        <SentimentDissatisfiedIcon /> Сесията ви е изтекла
      </DialogTitle>

      <DialogContent>
        <Typography>{MESSAGE}</Typography>
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "1.5rem",
          gap: 2,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            background: theme.palette.primary.main,
            "&:hover": {
              background: theme.palette.primary.dark,
            },
          }}
          variant="contained"
          size="medium"
        >
          ОК
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SessionExpiredDialogHost = () => {
  const redirect = useRedirect();
  const [state, setState] = React.useState(null);

  useEffect(() => {
    SessionExpiredDialogService._register(({ isOpen }) => {
      return new Promise(resolve => {
        setState({ isOpen, resolve });
      });
    });
  }, []);

  useEffect(() => {
    if (state?.isOpen) {
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      if (currentPath && currentPath !== "/login" && currentPath !== "/" && !currentPath.includes("/auth-callback")) {
        sessionStorage.setItem("redirectAfterLogin", currentPath);
      }
    }
  }, [state?.isOpen]);

  const onClose = () => {
    if (state && state.resolve) state.resolve();
    setState(null);
    removeTokens();
    redirect("login");
  };

  // Rendered outside <Admin>, so provide the tenant theme for the shared modal styles.
  const theme = useMemo(() => createTheme(getTheme(settings.tenant)), []);

  if (!settings.style.modernUi) {
    return (
      <LegacySessionExpiredDialog
        open={state?.isOpen === true}
        onClose={onClose}
      />
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={state?.isOpen === true}
        onClose={onClose}
        maxWidth="sm"
        sx={withModalSx()}
      >
        <ModalTitle
          icon={<SentimentDissatisfiedIcon />}
          title="Сесията ви е изтекла"
        />

        {/* Padding sits on the content, not the paper, so the gradient header reaches the edges.
            "&&" beats MUI's "DialogTitle + DialogContent { padding-top: 0 }" rule. */}
        <DialogContent sx={{ "&&": { px: { xs: "1rem", sm: "3rem" }, pt: { xs: "1rem", sm: "2rem" } } }}>
          <Typography>{MESSAGE}</Typography>
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "1.5rem",
            gap: 2,
          }}
        >
          <Button
            onClick={onClose}
            sx={modalConfirmButtonSx}
            variant="contained"
            size="medium"
          >
            ОК
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default SessionExpiredDialogHost;
